import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify'; // blog HTML is rendered on the public site, so strip scripts/handlers
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const CATEGORY_COLORS = {
  'Disease Surveillance': '#ef4444',
  'Public Health': '#3b82f6',
  'Vaccination': '#22c55e',
  'Health Equity': '#a855f7',
  'Epidemiology': '#f97316',
  'Data Analytics': '#0ea5e9',
  'Policy & Compliance': '#64748b',
  'Technology': '#8b5cf6',
  'Case Studies': '#eab308',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  const c = {
    pageBg:        isDark ? '#020617' : '#f8fafc',
    cardBg:        isDark ? '#0f172a' : '#ffffff',
    border:        isDark ? '#1e293b' : '#e2e8f0',
    textPrimary:   isDark ? '#f1f5f9' : '#0f172a',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    navBg:         isDark ? 'rgba(2,6,23,0.95)' : 'rgba(255,255,255,0.97)',
    navBorder:     isDark ? '#1e293b' : '#e2e8f0',
    accent:        '#00d4aa',
    tagBg:         isDark ? '#1e293b' : '#f1f5f9',
    tagColor:      isDark ? '#94a3b8' : '#475569',
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/blog/public/${slug}`);
        if (res.status === 404) { setError('not_found'); setLoading(false); return; }
        if (!res.ok) throw new Error('Failed to load post');
        const data = await res.json();
        setPost(data.post || data);
      } catch (err) {
        setError('error');
      }
      setLoading(false);
    };
    fetchPost();
  }, [slug]);

  const catColor = post ? (CATEGORY_COLORS[post.category] || '#64748b') : '#64748b';

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", background: c.pageBg, minHeight: '100vh', color: c.textPrimary }}>
      <style>{`
        * { box-sizing: border-box; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .blog-nav-link { color: ${c.textSecondary}; text-decoration: none; font-size: 14px; font-weight: 600; transition: color 0.2s; }
        .blog-nav-link:hover { color: #00d4aa; }
        .blog-content h2 { font-size: 28px; font-weight: 700; margin: 32px 0 16px; color: ${c.textPrimary}; font-family: 'DM Serif Display', serif; }
        .blog-content h3 { font-size: 22px; font-weight: 600; margin: 24px 0 12px; color: ${c.textPrimary}; }
        .blog-content p { margin-bottom: 16px; line-height: 1.8; color: ${c.textSecondary}; }
        .blog-content ul, .blog-content ol { margin: 16px 0 16px 24px; color: ${c.textSecondary}; }
        .blog-content li { margin-bottom: 8px; line-height: 1.7; }
        .blog-content strong { color: ${c.textPrimary}; font-weight: 700; }
        .blog-content blockquote { border-left: 4px solid #00d4aa; padding: 16px 24px; margin: 24px 0; background: rgba(0,212,170,0.05); border-radius: 0 8px 8px 0; font-style: italic; }
        .blog-content a { color: #00d4aa; text-decoration: underline; }
        .blog-content img { max-width: 100%; border-radius: 12px; margin: 24px 0; }
        .blog-content hr { border: none; border-top: 1px solid ${c.border}; margin: 32px 0; }
        .blog-content pre { background: ${isDark ? '#1e293b' : '#f1f5f9'}; border-radius: 10px; padding: 20px; overflow-x: auto; margin: 20px 0; }
        .blog-content code { font-family: monospace; font-size: 14px; color: #00d4aa; }
        .blog-content pre code { color: ${c.textPrimary}; }
        .back-link { color: #00d4aa; text-decoration: none; font-weight: 600; font-size: 14px; display: inline-flex; align-items: center; gap: 6px; transition: gap 0.2s; }
        .back-link:hover { gap: 10px; }
      `}</style>

      {/* ─── NAV ─── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        height: 68, padding: '0 48px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: scrolled ? c.navBg : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? `1px solid ${c.navBorder}` : 'none',
        transition: 'all 0.3s',
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10,
            background: 'linear-gradient(135deg, #00d4aa, #00b894)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 14, color: '#020617', fontFamily: "'DM Serif Display', serif",
          }}>ES</div>
          <span style={{ fontWeight: 800, fontSize: 17, color: c.textPrimary, letterSpacing: '-0.5px' }}>EpiSurveillance</span>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <Link to="/login" className="blog-nav-link">Sign In</Link>
          <Link to="/register" style={{
            padding: '8px 20px', borderRadius: 10,
            background: 'linear-gradient(135deg, #00d4aa, #00b894)',
            color: '#020617', fontWeight: 700, fontSize: 14,
            textDecoration: 'none', transition: 'opacity 0.2s',
          }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            Get Started
          </Link>
        </div>
      </nav>

      {/* ─── LOADING ─── */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: 40, height: 40, border: '4px solid #1e293b', borderTopColor: '#00d4aa', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
            <p style={{ color: c.textSecondary, fontSize: 14 }}>Loading article...</p>
          </div>
        </div>
      )}

      {/* ─── ERROR / NOT FOUND ─── */}
      {!loading && error && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', textAlign: 'center', padding: '0 24px' }}>
          <div style={{ fontSize: 64, marginBottom: 24 }}>{error === 'not_found' ? '🔍' : '⚠️'}</div>
          <h1 style={{ fontSize: 32, fontWeight: 800, fontFamily: "'DM Serif Display', serif", color: c.textPrimary, marginBottom: 12 }}>
            {error === 'not_found' ? 'Article Not Found' : 'Something Went Wrong'}
          </h1>
          <p style={{ color: c.textSecondary, fontSize: 16, marginBottom: 32, maxWidth: 400 }}>
            {error === 'not_found'
              ? "The article you're looking for doesn't exist or has been removed."
              : 'We had trouble loading this article. Please try again.'}
          </p>
          <div style={{ display: 'flex', gap: 12 }}>
            <Link to="/blog" style={{
              padding: '12px 28px', borderRadius: 10,
              background: c.accent, color: '#020617',
              fontWeight: 700, fontSize: 14, textDecoration: 'none',
            }}>
              ← Back to Blog
            </Link>
            {error !== 'not_found' && (
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '12px 28px', borderRadius: 10,
                  border: `1px solid ${c.border}`, background: 'transparent',
                  color: c.textPrimary, fontWeight: 600, fontSize: 14,
                  cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      )}

      {/* ─── POST CONTENT ─── */}
      {!loading && post && (
        <article style={{ paddingTop: 100, paddingBottom: 80, animation: 'fadeIn 0.5s ease' }}>
          {/* Header section */}
          <div style={{ maxWidth: 780, margin: '0 auto', padding: '0 24px 48px' }}>
            {/* Back link */}
            <Link to="/blog" className="back-link" style={{ marginBottom: 32, display: 'inline-flex' }}>
              ← Back to Blog
            </Link>

            {/* Category + read time */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24, marginBottom: 20 }}>
              <span style={{
                display: 'inline-block', padding: '5px 14px', borderRadius: 20,
                fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase',
                background: `${catColor}18`, color: catColor, border: `1px solid ${catColor}30`,
              }}>
                {post.category}
              </span>
              {post.readTime && (
                <span style={{ fontSize: 13, color: c.textSecondary }}>
                  {post.readTime} min read
                </span>
              )}
            </div>

            {/* Title */}
            <h1 style={{
              fontSize: 42, fontWeight: 800, fontFamily: "'DM Serif Display', serif",
              color: c.textPrimary, lineHeight: 1.25, margin: '0 0 24px',
            }}>
              {post.title}
            </h1>

            {/* Author + date */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 16,
              paddingBottom: 28, borderBottom: `1px solid ${c.border}`,
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%',
                background: 'linear-gradient(135deg, #00d4aa, #00b894)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: 14, color: '#020617',
                flexShrink: 0, fontFamily: "'DM Serif Display', serif",
              }}>
                ES
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: c.textPrimary }}>
                  EpiSurveillance Editorial
                </div>
                <div style={{ fontSize: 13, color: c.textSecondary, marginTop: 2 }}>
                  {formatDate(post.publishedAt || post.createdAt)}
                  {post.updatedAt && post.updatedAt !== post.createdAt && (
                    <span style={{ marginLeft: 8, opacity: 0.7 }}>
                      · Updated {formatDate(post.updatedAt)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Post body */}
          <div style={{ maxWidth: 780, margin: '0 auto', padding: '0 24px' }}>
            <div
              className="blog-content"
              style={{ fontSize: 17, lineHeight: 1.8, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content || '') }}
            />

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div style={{ marginTop: 48, paddingTop: 32, borderTop: `1px solid ${c.border}` }}>
                <p style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: c.textSecondary, marginBottom: 12 }}>
                  Tags
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {post.tags.map((tag, i) => (
                    <span
                      key={i}
                      style={{
                        padding: '6px 14px', borderRadius: 20,
                        background: c.tagBg, color: c.tagColor,
                        fontSize: 13, fontWeight: 500,
                        border: `1px solid ${c.border}`,
                      }}
                    >
                      #{tag.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Bottom back link */}
            <div style={{ marginTop: 48, paddingTop: 32, borderTop: `1px solid ${c.border}` }}>
              <Link to="/blog" className="back-link">
                ← Back to Blog
              </Link>
            </div>
          </div>
        </article>
      )}

      {/* ─── FOOTER ─── */}
      {!loading && (
        <footer style={{
          borderTop: `1px solid ${c.border}`,
          padding: '28px 48px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: isDark ? '#020617' : '#f8fafc',
        }}>
          <Link to="/" style={{ color: c.accent, textDecoration: 'none', fontWeight: 600, fontSize: 14 }}>
            ← Back to Home
          </Link>
          <p style={{ color: c.textSecondary, fontSize: 13, margin: 0 }}>
            © {new Date().getFullYear()} EpiSurveillance. All rights reserved.
          </p>
        </footer>
      )}
    </div>
  );
};

export default BlogPost;
